import {
  AttendanceCheckMonthParticipateStatus,
  AttendanceCheckParticipateErrorCodes,
} from '@diritto/common/activityPoint/constants';
import {
  AttendanceCheckDto,
  AttendanceCheckMonthDto,
  AttendanceCheckMonthParticipateDto,
} from '@diritto/common/activityPoint/dtos/attendance-check.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { numberComma } from '@diritto/common/utils/string';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { AttendanceCheckApi } from 'api/ApiHelper';
import axios, { AxiosError } from 'axios';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { CheckIcon } from 'icons/CheckIcon';
import { AttendanceStatus, getAttendanceCheckStatus } from 'libs/attendanceCheck';
import { useRouter } from 'next/navigation';
import { useGetUserMarketingAgreementQuery } from 'queries/account/useGetUserMarketingAgreementQuery';
import { activityPointQueryKey } from 'queries/activityPoint/queryKey';
import { useGetCouponsQuery } from 'queries/couponCatalog/useGetCouponQuery';
import React, { useCallback, useMemo } from 'react';
import { Button } from 'ui-components/common/Button';
import { Loading } from 'ui-components/common/Loading';
import { useRequestCertificate } from 'ui-hooks/useRequestCertificate';
import { useRequestMarketingAgreement } from 'ui-hooks/useRequestMarketingAgreement';
import { showAlertModal } from 'utils/modal';

export const RewardInfo = ({
  attendanceCheckMonth,
  attendanceCheckMonthParticipate,
  attends,
}: {
  attendanceCheckMonth: AttendanceCheckMonthDto;
  attendanceCheckMonthParticipate?: AttendanceCheckMonthParticipateDto;
  attends: AttendanceCheckDto[];
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { user } = useAuthState();
  const { tracker } = useTrackingState();

  const requestCertificate = useRequestCertificate();
  const requestMarketingAgreement = useRequestMarketingAgreement();

  const { data: marketingAgreementData } = useGetUserMarketingAgreementQuery();
  const { data: couponData, isInitialLoading: isCouponInitialLoading } = useGetCouponsQuery(
    attendanceCheckMonthParticipate?.extraInfo.coupons?.map(coupon => coupon.couponId) ?? [],
  );
  const couponPrice = couponData?.map(coupon => coupon.coupon.rewardInfo.price).reduce((acc, cur) => acc + cur, 0) ?? 0;

  const attendanceStatus = useMemo(
    () =>
      getAttendanceCheckStatus({
        attendanceCheckMonth,
        attendanceCheckMonthParticipate,
      }),
    [attendanceCheckMonth, attendanceCheckMonthParticipate],
  );

  const handleCheckAttendanceCheckReword = useCallback(async () => {
    if (!attendanceCheckMonthParticipate?.isParticipated) {
      return;
    }

    if (attendanceCheckMonthParticipate.status === AttendanceCheckMonthParticipateStatus.NotEnoughCondition) {
      showAlertModal(
        {
          title: `추첨 시 조건이 충족되지 않아 제외되었어요.`,
          description: `본인인증, 이벤트 / 추천 알림, ${attendanceCheckMonth.extraInfo.minAttendanceCount}일 출석체크 조건이 충족되지 않았어요.`,
        },
        { confirmText: '확인' },
      );
      return;
    }

    if (attendanceCheckMonthParticipate.status === AttendanceCheckMonthParticipateStatus.NotWin) {
      showAlertModal(
        {
          title: `아쉽지만 당첨이 되지 않았어요. 😭`,
          description: '다음 기회에 당첨되시기를 바랄게요. 🙏',
        },
        { confirmText: '확인' },
      );
      return;
    }

    if (attendanceCheckMonthParticipate.status === AttendanceCheckMonthParticipateStatus.Win) {
      if (!couponData) {
        return;
      }

      showAlertModal(
        {
          title: `축하드려요! 리디포인트 ${numberComma(couponPrice)}P에 당첨되었어요! 🎉`,
          description: `쿠폰 코드는 알림센터로 보내 드렸어요. 확인해 보세요!`,
        },
        {
          cancelText: '닫기',
          confirmText: '알림센터 가서 확인하기',
          onConfirm: () => router.push('/inbox'),
        },
      );
    }
  }, [
    attendanceCheckMonth.extraInfo.minAttendanceCount,
    attendanceCheckMonthParticipate?.isParticipated,
    attendanceCheckMonthParticipate?.status,
    couponData,
    couponPrice,
    router,
  ]);

  const handleParticipateAttendanceCheck = useCallback(async () => {
    if (!attendanceCheckMonth) {
      return;
    }

    tracker.track(TrackingEventType.ClickAttendanceCheckParticipate, {
      attendanceCheckMonthId: attendanceCheckMonth.id,
      yearMonth: attendanceCheckMonth.yearMonth,
    });

    try {
      await AttendanceCheckApi.participate(attendanceCheckMonth.id);
      queryClient
        .invalidateQueries({ queryKey: activityPointQueryKey.attendanceCheckParticipate(attendanceCheckMonth.id) })
        .then();
      queryClient.invalidateQueries({ queryKey: activityPointQueryKey.attendanceCheckMonths }).then();
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const errorCode = (e as AxiosError<{ code: AttendanceCheckParticipateErrorCodes }>).response?.data?.code;

        if (errorCode === AttendanceCheckParticipateErrorCodes.NeedUserCertification) {
          showAlertModal(
            {
              title: '본인인증이 되어 있지 않아요.',
              description:
                '출석체크 리워드에 당첨되려면 본인인증이 되어 있어야 해요.\n\n' +
                '대상자를 추첨하는 시점까지 당첨 계정 조건을 만족해야 리워드를 받을 수 있어요.',
            },
            {
              confirmText: '본인인증하러 가기',
              onConfirm: requestCertificate,
              cancelText: '닫기',
            },
          );
          return;
        }

        if (errorCode === AttendanceCheckParticipateErrorCodes.NeedMarketingAgreement) {
          showAlertModal(
            {
              title: '이벤트 / 추천 알림이 ON되어 있지 않아요.',
              description:
                '출석체크 리워드에 당첨되려면 이벤트 / 추천 알림이 ON되어 있어야 해요.\n\n' +
                '대상자를 추첨하는 시점까지 당첨 계정 조건을 만족해야 리워드를 받을 수 있어요.',
            },
            {
              confirmText: '이벤트 / 추천 알림 설정하러 가기',
              onConfirm: requestMarketingAgreement,
              cancelText: '닫기',
            },
          );
          return;
        }
      }

      showAlertModal(
        { title: '오류가 발생했습니다.', description: '다시 시도해 주세요.' },
        { confirmText: '다시 시도하기' },
      );
    }
  }, [attendanceCheckMonth, queryClient, requestCertificate, requestMarketingAgreement, tracker]);

  return (
    <>
      {attendanceStatus === AttendanceStatus.CanAttend && (
        <Button
          label="도전하기"
          key={AttendanceStatus.CanAttend}
          fullWidth
          onClick={handleParticipateAttendanceCheck}
        />
      )}
      {attendanceStatus === AttendanceStatus.Attended && (
        <Button label="도전 중!" key={AttendanceStatus.Attended} fullWidth disabled />
      )}
      {attendanceStatus === AttendanceStatus.Aggregating && (
        <Button label="집계 중!" key={AttendanceStatus.Aggregating} fullWidth disabled />
      )}
      {attendanceStatus === AttendanceStatus.RaffleCompleted &&
        (attendanceCheckMonthParticipate?.status === AttendanceCheckMonthParticipateStatus.Win &&
        isCouponInitialLoading ? (
          <div>
            <Loading />
          </div>
        ) : (
          <Button
            label="결과 확인"
            key={AttendanceStatus.RaffleCompleted}
            fullWidth
            onClick={handleCheckAttendanceCheckReword}
          />
        ))}

      {![AttendanceStatus.NotAvailEvent, AttendanceStatus.Past].includes(attendanceStatus) && attendanceCheckMonth && (
        <StyledRewardConditionWrapper>
          <StyledRewardConditionTitle>출석체크 리워드 당첨 조건</StyledRewardConditionTitle>
          <StyledRewardCondition>
            <StyledRewardConditionCheck checked={!!attendanceCheckMonthParticipate?.isParticipated}>
              <CheckIcon />
            </StyledRewardConditionCheck>
            도전하기 버튼 누르기
          </StyledRewardCondition>
          <StyledRewardCondition>
            <StyledRewardConditionCheck checked={!!user?.ageVerified}>
              <CheckIcon />
            </StyledRewardConditionCheck>
            본인인증
          </StyledRewardCondition>
          <StyledRewardCondition>
            <StyledRewardConditionCheck checked={!!marketingAgreementData?.userMarketingAgreement.agree}>
              <CheckIcon />
            </StyledRewardConditionCheck>
            이벤트 / 추천 알림
          </StyledRewardCondition>
          <StyledRewardCondition>
            <StyledRewardConditionCheck
              checked={(attends.length ?? 0) >= attendanceCheckMonth.extraInfo.minAttendanceCount}>
              <CheckIcon />
            </StyledRewardConditionCheck>
            {attendanceCheckMonth.extraInfo.minAttendanceCount}일 출석
          </StyledRewardCondition>
        </StyledRewardConditionWrapper>
      )}
    </>
  );
};

const StyledRewardConditionWrapper = styled.div`
  margin-top: 48px;
`;

const StyledRewardConditionTitle = styled.div`
  color: ${Color.Black};
  font-weight: ${FontWeight.Bold};
  ${Typography.Small};
`;

const StyledRewardCondition = styled.div`
  margin-top: 16px;
  color: ${Color.Gray600};
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};

  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledRewardConditionCheck = styled.div(
  ({ checked }: { checked: boolean }) => css`
    width: 18px;
    height: 18px;

    display: flex;
    align-items: center;
    justify-content: center;

    background: ${checked ? Color.Green400 : Color.Gray300};
    border-radius: 200px;
  `,
);
